'use no memo';
import React from 'react';
import { css } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Pressable, { type PressableProps } from '@atlaskit/primitives/pressable';
import { token } from '@atlaskit/tokens';
import { fg } from '@confluence/feature-gating';

import type { SortOrders } from '../pprTypes.ts';

const sortIconBaseStylesFallback = css({
	position: 'relative',
	'&::before': {
		display: 'block',
		width: '0',
		height: '0',
		position: 'absolute',
		borderColor: 'transparent',
		borderStyle: 'solid',
		borderWidth: '3px',
		content: '""',
		borderBlockEnd: `3px solid ${token('color.icon.disabled')}`,
		insetBlockEnd: token('space.100'),
		insetInlineEnd: token('space.negative.100'),
		top: '7px',
	},
	'&::after': {
		display: 'block',
		width: '0',
		height: '0',
		position: 'absolute',
		borderColor: 'transparent',
		borderStyle: 'solid',
		borderWidth: '3px',
		content: '""',
		borderBlockStart: `3px solid ${token('color.icon.disabled')}`,
		insetBlockEnd: '0',
		insetInlineEnd: token('space.negative.100'),
		top: '14px',
	},
});

const sortIconBaseStylesTokenized = css({
	position: 'relative',
	'&::before': {
		display: 'block',
		width: '0',
		height: '0',
		position: 'absolute',
		borderColor: 'transparent',
		borderStyle: 'solid',
		borderWidth: token('border.width.focused'),
		content: '""',
		borderBlockEnd: `${token('border.width.focused')} solid ${token('color.icon.disabled')}`,
		insetBlockEnd: token('space.100'),
		insetInlineEnd: token('space.negative.100'),
		top: '7px',
	},
	'&::after': {
		display: 'block',
		width: '0',
		height: '0',
		position: 'absolute',
		borderColor: 'transparent',
		borderStyle: 'solid',
		borderWidth: token('border.width.focused'),
		content: '""',
		borderBlockStart: `${token('border.width.focused')} solid ${token('color.icon.disabled')}`,
		insetBlockEnd: '0',
		insetInlineEnd: token('space.negative.100'),
		top: '14px',
	},
});

const sortIconAscendingStylesFallback = css({
	'&::before': {
		borderBlockEnd: `3px solid ${token('color.icon.subtle')}`,
	},
});

const sortIconAscendingStylesTokenized = css({
	'&::before': {
		borderBlockEnd: `${token('border.width.focused')} solid ${token('color.icon.subtle')}`,
	},
});

const sortIconDescendingStylesFallback = css({
	'&::after': {
		borderBlockStart: `3px solid ${token('color.icon.subtle')}`,
	},
});

const sortIconDescendingStylesTokenized = css({
	'&::after': {
		borderBlockStart: `${token('border.width.focused')} solid ${token('color.icon.subtle')}`,
	},
});

const headerPressableStyles = xcss({
	background: 'none',
	font: 'font.body.large',
	fontWeight: 'font.weight.bold',
	color: 'color.text',
	display: 'flex',
	gap: 'space.100',
});

type HeaderPressableProps = PressableProps & { sortOrder?: SortOrders };

export const HeaderPressable: (props: HeaderPressableProps) => JSX.Element = ({
	children,
	sortOrder,
	...pressableProps
}) => {
	return (
		// TODO: Update the attribute below so it is translated.
		/* oxlint-disable-next-line eslint-core/no-restricted-syntax */ // eslint-disable-next-line no-restricted-syntax
		<Pressable aria-roledescription="Sort button" xcss={headerPressableStyles} {...pressableProps}>
			{children}
			{fg('confluence_frontend_tokenize_border') ? (
				<span
					aria-hidden="true"
					data-testid="page-properties-report-sort-icon"
					data-sort-order={sortOrder}
					css={
						// sortOrder is undefined when a header is not sorted, so we want to hide the sort icon
						!!sortOrder && [
							sortIconBaseStylesTokenized,
							sortOrder === 'ascending' && sortIconAscendingStylesTokenized,
							sortOrder === 'descending' && sortIconDescendingStylesTokenized,
						]
					}
				/>
			) : (
				<span
					aria-hidden="true"
					data-testid="page-properties-report-sort-icon"
					data-sort-order={sortOrder}
					css={
						!!sortOrder && [
							sortIconBaseStylesFallback,
							sortOrder === 'ascending' && sortIconAscendingStylesFallback,
							sortOrder === 'descending' && sortIconDescendingStylesFallback,
						]
					}
				/>
			)}
		</Pressable>
	);
};
